import type { ReactNode } from 'react'
import type { CorporateActionItem } from '@/lib/market/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { formatIsoDate } from '@/lib/market/calendar'
import { formatPrice } from '@/lib/market/format'

interface CorporateActionsProps {
    items: CorporateActionItem[]
    today: string
    showCompany?: boolean
}

const statusOf = (item: CorporateActionItem, today: string): { label: string, tone: string } => {
    if (item.bookClosure && item.bookClosure >= today) return { label: 'Register open', tone: 'bg-primary/12 text-primary' }
    if (item.paymentDate && item.paymentDate >= today) return { label: 'Awaiting payment', tone: 'bg-series-1/15 text-series-1' }

    return { label: 'Paid', tone: 'bg-secondary text-muted-foreground' }
}

const CorporateActions = ({ items, today, showCompany = true }: CorporateActionsProps): ReactNode => {
    if (!items.length) {
        return (
            <p className='rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground'>
                No corporate actions on record.
            </p>
        )
    }

    return (
        <div className='-mx-4 overflow-x-auto px-4 scroll-thin sm:mx-0 sm:px-0'>
            <table className='w-full min-w-[520px] text-left text-[13px]'>
                <caption className='sr-only'>Dividends and corporate actions</caption>
                <thead>
                    <tr className='border-b border-border text-[11px] uppercase tracking-[0.06em] text-muted-foreground'>
                        {showCompany && <th scope='col' className='py-2 pr-3 font-medium'>Company</th>}
                        <th scope='col' className='py-2 pr-3 font-medium'>Action</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>KES per share</th>
                        <th scope='col' className='py-2 pr-3 font-medium'>Books close</th>
                        <th scope='col' className='py-2 pr-3 font-medium'>Payment</th>
                        <th scope='col' className='py-2 font-medium'>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => {
                        const status = statusOf(item, today)

                        return (
                            <tr key={item.id} className='border-b border-border/60 last:border-0'>
                                {showCompany && (
                                    <td className='py-2.5 pr-3'>
                                        {item.companySlug ? (
                                            <Link href={`/stocks/${item.companySlug}`} className='group block'>
                                                <span className='font-mono text-xs font-semibold text-foreground group-hover:text-primary'>{item.companyTicker}</span>
                                                {item.companyName && item.companyId && (
                                                    <span className='block max-w-48 truncate text-xs text-muted-foreground'>
                                                        <span data-mdlfy-cms={`companies:${item.companyId}:name`}>{item.companyName}</span>
                                                    </span>
                                                )}
                                            </Link>
                                        ) : <span className='text-muted-foreground'>-</span>}
                                    </td>
                                )}
                                <td className='py-2.5 pr-3 text-foreground'>
                                    <span data-mdlfy-cms={`corporate_actions:${item.id}:action_type`}>{item.actionType}</span>
                                    {item.ratio && <span className='ml-1 text-muted-foreground' data-mdlfy-cms={`corporate_actions:${item.id}:ratio`}>{item.ratio}</span>}
                                </td>
                                <td className='py-2.5 pr-3 text-right tabular-nums text-foreground'>{item.amount !== null ? formatPrice(item.amount) : '-'}</td>
                                <td className='py-2.5 pr-3 tabular-nums text-muted-foreground'>{formatIsoDate(item.bookClosure)}</td>
                                <td className='py-2.5 pr-3 tabular-nums text-muted-foreground'>{formatIsoDate(item.paymentDate)}</td>
                                <td className='py-2.5'>
                                    <span className={cn('inline-flex whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium', status.tone)}>{status.label}</span>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default CorporateActions
